export const termsContent = {
  title: "Terms of Service",
  lastUpdated: "December 7, 2025",
  sections: [
    {
      title: "Acceptance of Terms",
      content: `By accessing or using Zentrix Insights ("the Service"), you agree to be bound by these Terms of Service ("Terms"). If you do not agree to these Terms, you may not access or use the Service.

These Terms apply to all visitors, users, and others who access or use the Service. By using the Service on behalf of an organization, you represent that you have authority to bind that organization to these Terms.`
    },
    {
      title: "Description of Service",
      content: `Zentrix Insights provides behavioral and cognitive assessment tools designed to help individuals and organizations understand personality traits, work styles, and cognitive abilities. Our services include:

• **Behavioral Assessments**: ABCDE personality profiling based on behavioral preferences
• **Cognitive Assessments**: Aptitude testing for verbal, numerical, and abstract reasoning
• **Team Analytics**: Tools for analyzing team composition and dynamics
• **Reporting**: Individual and organizational reports and insights
• **AI-Powered Insights**: Machine learning-enhanced analysis and recommendations

We reserve the right to modify, suspend, or discontinue any aspect of the Service at any time.`
    },
    {
      title: "Account Registration",
      content: `To access certain features, you must create an account. You agree to:

• Provide accurate, current, and complete information during registration
• Maintain and promptly update your account information
• Keep your password secure and confidential
• Notify us immediately of any unauthorized access
• Accept responsibility for all activities under your account

We reserve the right to suspend or terminate accounts that violate these Terms or for any other reason at our sole discretion.`
    },
    {
      title: "Subscription and Billing",
      content: `**Free Trial**: New accounts receive a 7-day free trial with full access to features. No credit card is required to start.

**Paid Subscriptions**: After the trial period, continued access requires a paid subscription. Subscription fees are billed monthly or annually as selected.

**Payment**: All payments are processed securely through Stripe. By subscribing, you authorize us to charge your payment method on a recurring basis.

**Cancellation**: You may cancel your subscription at any time. Cancellation takes effect at the end of the current billing period. No refunds are provided for partial billing periods.

**Price Changes**: We may change subscription prices with 30 days' notice. Continued use after the price change constitutes acceptance.`
    },
    {
      title: "Acceptable Use",
      content: `You agree not to use the Service to:

• Violate any applicable laws or regulations
• Infringe on intellectual property rights of others
• Transmit harmful, offensive, or fraudulent content
• Attempt to gain unauthorized access to any systems
• Interfere with or disrupt the Service or servers
• Use the Service for competitive analysis or to build a competing product
• Share assessment content or questions with unauthorized parties
• Manipulate or game assessment results
• Resell or redistribute the Service without authorization`
    },
    {
      title: "Assessment Integrity",
      content: `To maintain the validity of our assessments:

• You must complete assessments honestly and without assistance
• Each assessment should be completed by the intended individual only
• Assessment questions and content are confidential and proprietary
• Sharing, copying, or reproducing assessment content is prohibited
• We may invalidate results if we detect manipulation or fraud

Assessment results are provided for informational purposes and should not be the sole basis for employment or other significant decisions.`
    },
    {
      title: "Intellectual Property",
      content: `**Our Property**: The Service, including all content, features, functionality, software, and trademarks, is owned by Zentrix Insights and protected by intellectual property laws. You may not copy, modify, distribute, or create derivative works without our written permission.

**Your Content**: You retain ownership of any content you submit. By submitting content, you grant us a worldwide, non-exclusive license to use, store, and process it to provide the Service.

**Feedback**: Any feedback, suggestions, or ideas you provide may be used by us without obligation or compensation.`
    },
    {
      title: "Privacy and Data",
      content: `Your privacy is important to us. Our collection and use of personal information is governed by our Privacy Policy, which is incorporated into these Terms by reference. By using the Service, you consent to our data practices as described in the Privacy Policy.

For enterprise accounts, additional data processing terms may apply as outlined in a separate Data Processing Agreement.`
    },
    {
      title: "Disclaimer of Warranties",
      content: `THE SERVICE IS PROVIDED "AS IS" AND "AS AVAILABLE" WITHOUT WARRANTIES OF ANY KIND, EXPRESS OR IMPLIED, INCLUDING BUT NOT LIMITED TO:

• Merchantability or fitness for a particular purpose
• Accuracy, reliability, or completeness of content
• Uninterrupted or error-free operation
• Security of data transmission

Assessment results are based on self-reported data and statistical models. They provide insights but are not diagnostic tools. We do not guarantee any specific outcomes from using the Service.`
    },
    {
      title: "Limitation of Liability",
      content: `TO THE MAXIMUM EXTENT PERMITTED BY LAW, ZENTRIX INSIGHTS SHALL NOT BE LIABLE FOR:

• Indirect, incidental, special, consequential, or punitive damages
• Loss of profits, revenue, data, or business opportunities
• Damages arising from your use or inability to use the Service
• Actions of third parties or other users
• Decisions made based on assessment results

Our total liability for any claims shall not exceed the amount you paid us in the twelve months preceding the claim.`
    },
    {
      title: "Indemnification",
      content: `You agree to indemnify, defend, and hold harmless Zentrix Insights and its officers, directors, employees, and agents from any claims, damages, losses, liabilities, and expenses (including legal fees) arising from:

• Your use of the Service
• Your violation of these Terms
• Your violation of any third-party rights
• Any content you submit to the Service`
    },
    {
      title: "Termination",
      content: `We may terminate or suspend your account and access to the Service immediately, without prior notice, for any reason, including:

• Breach of these Terms
• Request by law enforcement or government agency
• Extended periods of inactivity
• Technical or security issues
• Non-payment of fees

Upon termination, your right to use the Service ceases immediately. You may request export of your data within 30 days of termination.`
    },
    {
      title: "Governing Law",
      content: `These Terms shall be governed by and construed in accordance with the laws of [Your Jurisdiction], without regard to conflict of law principles. Any disputes shall be resolved in the courts of [Your Jurisdiction].

For users in the European Union, nothing in these Terms affects your statutory rights as a consumer under applicable EU law.`
    },
    {
      title: "Changes to Terms",
      content: `We reserve the right to modify these Terms at any time. We will notify you of material changes by:

• Posting the updated Terms on our website
• Updating the "Last Updated" date
• Sending email notification for significant changes

Your continued use of the Service after changes constitutes acceptance of the new Terms.`
    },
    {
      title: "Contact Information",
      content: `For questions about these Terms, please contact us:

**Email**: legal@zentrixinsights.com
**Address**: Zentrix Insights, [Your Address]

For support inquiries, visit our Support page or email support@zentrixinsights.com.`
    }
  ]
};
